// Perfil metrológico de balanzas monoplato.
// Convención única del sistema: error = indicación del instrumento - valor del patrón.

const SQRT3 = Math.sqrt(3);

export const MASS_BALANCE_TEMPLATE = {
  code: 'MASS-BALANCE:monoplato:v1',
  revision: '2',
  family: 'mass',
  subfamily: 'balance',
  title: 'Calibración de balanzas monoplato',
  unit: 'kg',
  unitLabel: '[kg]',
  pointCount: 7,
  repeatCount: 5,
  method: 'Por comparación directa con masas patrón certificadas.',
  procedure: 'P-DT-PT-25-00 (Calibración de Balanzas Monoplato)',
};

export const MASS_REPEATABILITY_KEYS = ['a1', 'a2', 'a3', 'a4', 'a5'];
export const MASS_ECCENTRICITY_KEYS = ['b', 'c', 'd', 'e'];

export const DEFAULT_MASS_FAMILY_DATA = {
  profile_key: 'mass_balance_monoplato',
  profile_label: 'Masa · Balanza monoplato',
  method: MASS_BALANCE_TEMPLATE.method,
  procedure: MASS_BALANCE_TEMPLATE.procedure,
  point_count: MASS_BALANCE_TEMPLATE.pointCount,
  balance_class: '',
  decision_rule: 'guard_band',
  standard_configuration: { weights: [] },
  point_standards: [],
  standard_budget: {
    drift_factor: 0,
    convection_uncertainty_kg: 0,
    other_uncertainty_kg: 0,
  },
  environment_readings: [
    { key: 'start_1', label: 'Inicio 1.er ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'start_2', label: 'Inicio 2.º ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'end_2', label: 'Fin 2.º ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
  ],
  result_details: [],
};

export function finiteOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

export function normalizeMassUnit(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, '');
}

export function massToKg(value, unit) {
  const numeric = finiteOrNull(value);
  if (numeric === null) return null;
  const normalized = normalizeMassUnit(unit);
  if (normalized === 'kg') return numeric;
  if (normalized === 'g' || normalized === 'gr') return numeric / 1000;
  if (normalized === 'mg') return numeric / 1_000_000;
  if (normalized === 't' || normalized === 'tn') return numeric * 1000;
  if (normalized === 'lb' || normalized === 'lbs') return numeric * 0.45359237;
  throw new Error(`La unidad ${unit || '(sin unidad)'} no es compatible con el perfil de Masa.`);
}

function mean(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  if (!valid.length) return null;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function sampleStandardDeviation(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  if (valid.length < 2) return 0;
  const average = mean(valid);
  const variance = valid.reduce((sum, value) => sum + ((value - average) ** 2), 0) / (valid.length - 1);
  return Math.sqrt(variance);
}

function rss(values = []) {
  return Math.sqrt(values.reduce((sum, value) => {
    const number = finiteOrNull(value) ?? 0;
    return sum + (number ** 2);
  }, 0));
}

export function mergeMassFamilyData(value = {}) {
  const input = value && typeof value === 'object' ? value : {};
  const budget = input.standard_budget && typeof input.standard_budget === 'object'
    ? input.standard_budget
    : {};
  const environment = Array.isArray(input.environment_readings)
    ? input.environment_readings
    : DEFAULT_MASS_FAMILY_DATA.environment_readings;
  return {
    ...DEFAULT_MASS_FAMILY_DATA,
    ...input,
    standard_configuration: {
      ...DEFAULT_MASS_FAMILY_DATA.standard_configuration,
      ...(input.standard_configuration || {}),
    },
    standard_budget: {
      ...DEFAULT_MASS_FAMILY_DATA.standard_budget,
      ...budget,
    },
    point_standards: Array.isArray(input.point_standards) ? input.point_standards : [],
    environment_readings: DEFAULT_MASS_FAMILY_DATA.environment_readings.map((fallback, index) => ({
      ...fallback,
      ...(environment[index] || environment.find((item) => item?.key === fallback.key) || {}),
    })),
    result_details: Array.isArray(input.result_details) ? input.result_details : [],
  };
}

export function buildMassBalanceBlock(pointCount = MASS_BALANCE_TEMPLATE.pointCount) {
  return {
    block_key: 'mass_balance',
    display_name: 'Balanza monoplato',
    unit: MASS_BALANCE_TEMPLATE.unitLabel,
    applicable: true,
    point_count: Math.max(3, Math.min(7, Number(pointCount) || MASS_BALANCE_TEMPLATE.pointCount)),
    sort_order: 0,
    mode: 'mass_balance',
  };
}

export function suggestMassBalanceSetpoints(rangeMin, rangeMax, count = MASS_BALANCE_TEMPLATE.pointCount) {
  const min = Math.max(0, finiteOrNull(rangeMin) ?? 0);
  const max = finiteOrNull(rangeMax);
  if (max === null || max <= min) return Array.from({ length: count }, () => '');
  const fractions = count <= 3
    ? [0.1, 0.5, 1]
    : count === 5
      ? [0.05, 0.25, 0.5, 0.75, 1]
      : [0.02, 0.1, 0.25, 0.5, 0.75, 0.9, 1];
  return fractions.slice(0, count).map((fraction) => Number((min + ((max - min) * fraction)).toPrecision(8)));
}

export function standardWeightValues(snapshot = {}) {
  const capability = snapshot.capability || {};
  const metadata = capability.metadata || {};
  const result = Array.isArray(snapshot.results) ? snapshot.results[0] || {} : {};
  const nominalKg = finiteOrNull(metadata.nominal_kg) ?? finiteOrNull(result.reference_value);
  const errorKg = finiteOrNull(result.error) ?? 0;
  const actualKg = finiteOrNull(result.indicated_value)
    ?? (nominalKg === null ? null : nominalKg + errorKg);
  const expandedKg = Math.abs(finiteOrNull(result.uncertainty_expanded) ?? 0);
  const coverageFactor = finiteOrNull(result.coverage_factor)
    ?? finiteOrNull(snapshot.certificate?.coverage_factor)
    ?? 2;
  const mpeKg = Math.abs(finiteOrNull(metadata.mpe_mg) ?? 0) / 1_000_000;
  return {
    capabilityId: capability.id,
    code: snapshot.asset?.code || '',
    serial: snapshot.asset?.serial || '',
    nominalKg,
    errorKg,
    actualKg,
    expandedKg,
    coverageFactor,
    mpeKg,
  };
}

export function findBestMassWeightCombination(targetKg, options = [], toleranceKg = 0) {
  const target = finiteOrNull(targetKg);
  if (target === null || target <= 0) return [];
  const weights = options
    .map((option) => ({
      id: option.capability_id || option.capabilityId || option.id,
      value: finiteOrNull(option.nominal_kg ?? option.nominalKg),
    }))
    .filter((item) => item.id && item.value !== null && item.value > 0);
  let best = null;
  const combinations = 1 << weights.length;
  for (let mask = 1; mask < combinations; mask += 1) {
    let sum = 0;
    const ids = [];
    for (let index = 0; index < weights.length; index += 1) {
      if (mask & (1 << index)) {
        sum += weights[index].value;
        ids.push(weights[index].id);
      }
    }
    const delta = Math.abs(sum - target);
    if (!best || delta < best.delta || (delta === best.delta && ids.length < best.ids.length)) {
      best = { delta, sum, ids };
    }
  }
  if (!best) return [];
  const allowed = Math.max(Math.abs(finiteOrNull(toleranceKg) ?? 0), Math.abs(target) * 1e-9, 1e-12);
  return best.delta <= allowed ? best.ids : [];
}

export function resolveMassPointStandards(pointNo, familyData = {}) {
  const merged = mergeMassFamilyData(familyData);
  const row = merged.point_standards.find((item) => Number(item?.point_no) === Number(pointNo));
  return Array.isArray(row?.capability_ids) ? row.capability_ids.filter(Boolean) : [];
}

export function computeMassBalancePointMetrics(row = {}, session = {}, allWeightSnapshots = []) {
  const familyData = mergeMassFamilyData(session.family_data);
  const selectedIds = Array.isArray(row.capability_ids) ? row.capability_ids : [];
  const weights = allWeightSnapshots
    .filter((snapshot) => selectedIds.includes(snapshot?.capability?.id))
    .map(standardWeightValues);
  if (!weights.length) return null;

  const repeatabilityReadings = MASS_REPEATABILITY_KEYS
    .map((key) => finiteOrNull(row[key]))
    .filter((value) => value !== null);
  const eccentricityReadings = MASS_ECCENTRICITY_KEYS
    .map((key) => finiteOrNull(row[key]))
    .filter((value) => value !== null);
  if (repeatabilityReadings.length !== MASS_REPEATABILITY_KEYS.length
      || eccentricityReadings.length !== MASS_ECCENTRICITY_KEYS.length) return null;

  const patternMean = weights.reduce((sum, weight) => sum + (weight.actualKg ?? 0), 0);
  const nominalPattern = weights.reduce((sum, weight) => sum + (weight.nominalKg ?? 0), 0);
  const instrumentMean = mean(repeatabilityReadings);
  const errorAbs = instrumentMean - patternMean;
  const errorPct = patternMean === 0 ? null : (errorAbs / patternMean) * 100;
  const repeatability = sampleStandardDeviation(repeatabilityReadings);

  const centerReading = finiteOrNull(row.a1) ?? instrumentMean;
  const eccentricityError = Math.max(...eccentricityReadings.map((value) => Math.abs(value - centerReading)));

  const resolution = Math.abs(finiteOrNull(session.instrument_resolution) ?? 0);
  const uResolutionZero = resolution / (2 * SQRT3);
  const uResolutionLoad = resolution / (2 * SQRT3);
  const uResolution = rss([uResolutionZero, uResolutionLoad]);
  const uStandard = weights.reduce(
    (sum, weight) => sum + (weight.coverageFactor > 0 ? weight.expandedKg / weight.coverageFactor : 0),
    0,
  );
  const driftFactor = Math.max(0, finiteOrNull(familyData.standard_budget.drift_factor) ?? 1);
  const uDrift = weights.reduce((sum, weight) => sum + ((weight.expandedKg * driftFactor) / SQRT3), 0);
  const uEccentricity = eccentricityError / (2 * SQRT3);
  const uAirBuoyancy = weights.reduce((sum, weight) => sum + (weight.mpeKg / (4 * SQRT3)), 0);
  const uConvection = Math.abs(finiteOrNull(familyData.standard_budget.convection_uncertainty_kg) ?? 0) / SQRT3;
  const uOther = Math.abs(finiteOrNull(familyData.standard_budget.other_uncertainty_kg) ?? 0) / SQRT3;
  const uRepeatability = repeatability / Math.sqrt(repeatabilityReadings.length);
  const uc = rss([
    uStandard,
    uDrift,
    uResolution,
    uRepeatability,
    uEccentricity,
    uAirBuoyancy,
    uConvection,
    uOther,
  ]);
  const k = 2;
  const expanded = uc * k;
  const tolerance = Math.abs(finiteOrNull(session.instrument_class) ?? 0);
  const inTolerance = tolerance > 0 ? Math.abs(errorAbs) + expanded <= tolerance : null;

  return {
    point_no: Number(row.point_no),
    nominal_pattern_kg: nominalPattern,
    pattern_mean: patternMean,
    instrument_mean: instrumentMean,
    error_abs: errorAbs,
    correction: -errorAbs,
    error_pct: errorPct,
    repeatability,
    eccentricity_error: eccentricityError,
    u_standard: uStandard,
    u_drift: uDrift,
    u_resolution: uResolution,
    u_repeatability: uRepeatability,
    u_eccentricity: uEccentricity,
    u_air_buoyancy: uAirBuoyancy,
    u_convection: uConvection,
    u_other: uOther,
    uc,
    k,
    u_expanded: expanded,
    u_reported: Math.max(expanded, resolution), // piso por resolucion del instrumento (QA 2026-09-06)
    in_tolerance: inTolerance,
    capability_ids: selectedIds,
    pattern_codes: weights.map((weight) => weight.code),
  };
}

export function validateMassBalanceRows(rows = [], session = {}, snapshots = []) {
  const issues = [];
  const rangeMin = finiteOrNull(session.instrument_range_min);
  const rangeMax = finiteOrNull(session.instrument_range_max);
  const resolution = Math.abs(finiteOrNull(session.instrument_resolution) ?? 0);
  rows.forEach((row, index) => {
    const point = Number(row.point_no) || index + 1;
    const setpoint = finiteOrNull(row.setpoint);
    if (setpoint === null) issues.push(`Punto ${point}: falta indicar la masa nominal.`);
    else if ((rangeMin !== null && setpoint < rangeMin) || (rangeMax !== null && setpoint > rangeMax)) {
      issues.push(`Punto ${point}: la masa nominal está fuera del intervalo declarado de la balanza.`);
    }
    const selected = Array.isArray(row.capability_ids) ? row.capability_ids : [];
    if (!selected.length) issues.push(`Punto ${point}: falta asignar una o más masas patrón.`);
    const repeats = MASS_REPEATABILITY_KEYS.map((key) => finiteOrNull(row[key]));
    if (repeats.some((value) => value === null)) issues.push(`Punto ${point}: se requieren las cinco lecturas de repetibilidad.`);
    const eccentricity = MASS_ECCENTRICITY_KEYS.map((key) => finiteOrNull(row[key]));
    if (eccentricity.some((value) => value === null)) issues.push(`Punto ${point}: se requieren las cuatro posiciones de excentricidad B–E.`);
    const weights = snapshots
      .filter((snapshot) => selected.includes(snapshot?.capability?.id))
      .map(standardWeightValues);
    if (selected.length && weights.length !== selected.length) {
      issues.push(`Punto ${point}: una de las masas seleccionadas no tiene trazabilidad vigente.`);
    }
    if (setpoint !== null && weights.length) {
      const selectedNominal = weights.reduce((sum, weight) => sum + (weight.nominalKg ?? 0), 0);
      const allowed = Math.max(resolution, Math.abs(setpoint) * 1e-9, 1e-12);
      if (Math.abs(selectedNominal - setpoint) > allowed) {
        issues.push(`Punto ${point}: la suma nominal de las masas patrón (${selectedNominal} kg) no coincide con ${setpoint} kg.`);
      }
    }
    const metrics = computeMassBalancePointMetrics(row, session, snapshots);
    if (selected.length && !metrics) issues.push(`Punto ${point}: no se pudo resolver el valor certificado del patrón.`);
  });
  return issues;
}
